import React, { useRef } from 'react'

import { ReactComponent as AddSmallIcon } from '../../assets/icons/add-small.svg'
import { ReactComponent as AddMediumIcon } from '../../assets/icons/add-medium.svg'
import { ReactComponent as AddLargeIcon } from '../../assets/icons/add-large.svg'

import { ReactComponent as SubtractSmallIcon } from '../../assets/icons/subtract-small.svg'
import { ReactComponent as SubtractMediumIcon } from '../../assets/icons/subtract-medium.svg'
import { ReactComponent as SubtractLargeIcon } from '../../assets/icons/subtract-large.svg'

import { ReactComponent as UndoSmallIcon } from '../../assets/icons/undo-small.svg'
import { ReactComponent as UndoMediumIcon } from '../../assets/icons/undo-medium.svg'
import { ReactComponent as UndoLargeIcon } from '../../assets/icons/undo-large.svg'

import { ReactComponent as RedoSmallIcon } from '../../assets/icons/redo-small.svg'
import { ReactComponent as RedoMediumIcon } from '../../assets/icons/redo-medium.svg'
import { ReactComponent as RedoLargeIcon } from '../../assets/icons/redo-large.svg'

import { ReactComponent as MoreSmallIcon } from '../../assets/icons/more-small.svg'
import { ReactComponent as MoreMediumIcon } from '../../assets/icons/more-medium.svg'
import { ReactComponent as MoreLargeIcon } from '../../assets/icons/more-large.svg'

import { ReactComponent as TrashSmallIcon } from '../../assets/icons/trash-small.svg'
import { ReactComponent as TrashMediumIcon } from '../../assets/icons/trash-medium.svg'
import { ReactComponent as TrashLargeIcon } from '../../assets/icons/trash-large.svg'

import {
  addCoordinates,
  adjustTranslateCoordinate,
  getTranslateDelta,
  SCOPE_MAX,
  SCOPE_MIN,
} from '../../utils/utils'

import { updateHistoryStackReset } from '../../utils/undoRedoUtils'

export default function BottomPanel({
  boardId,
  isWritable,
  ///////
  paths,
  setPaths,
  // scope && translate
  scope,
  setScope,
  yScopeRef,
  yTranslateRef,
  translate,
  setTranslate,
  scaleRatio,
  historyStack,
  setHistoryStack,
  selectionRectPathKeys,
  setSelectionRectPathKeys,
  // <more button>
  isMoreButtonVisible,
  setIsMoreButtonVisible,
  lockedCheckKey,
  setLockedCheckKey,
  // 동시편집
  ydocRef,
  yPathsRef,
  triggerAddPath,
  triggerUpdatePath,
  triggerUpdateTranslateAndScope,
}) {
  const translateRef = useRef(translate)

  const zoomInOut = (mode) => {
    let nextScope

    if (mode === 'Out') {
      nextScope =
        Math.round((scope - 0.1) * 10) / 10 <= SCOPE_MIN * scaleRatio.current
          ? SCOPE_MIN * scaleRatio.current
          : Math.round((scope - 0.1) * 10) / 10
    } else if (mode === 'In') {
      nextScope =
        Math.round((scope + 0.1) * 10) / 10 >= SCOPE_MAX * scaleRatio.current
          ? SCOPE_MAX * scaleRatio.current
          : Math.round((scope + 0.1) * 10) / 10
    } else {
      console.log('zoomInOut() error')
    }

    const translateDelta = getTranslateDelta(
      window.innerWidth / 2,
      window.innerHeight / 2,
      nextScope,
      scope
    )
    setScope(nextScope)
    // yScopeRef.current?.set('scope', nextScope / scaleRatio.current)

    setTranslate((prev) => {
      const updateTranslate = adjustTranslateCoordinate(
        addCoordinates(prev, translateDelta),
        nextScope,
        scaleRatio.current
      )

      translateRef.current = updateTranslate
      return updateTranslate
    })
    // setTranslate((prev) => addCoordinates(prev, translateDelta))
    // let tempTranslate = addCoordinates(translate, translateDelta)
    // setTranslate(tempTranslate)
    // yTranslateRef.current.set('translateX', tempTranslate.x)
    // yTranslateRef.current.set('translateY', tempTranslate.y)
    requestAnimationFrame(() => {
      triggerUpdateTranslateAndScope(translateRef.current, nextScope)
    })
  }

  const onClickReset = () => {
    setIsMoreButtonVisible(false)
    if (lockedCheckKey) setLockedCheckKey('')
    if (selectionRectPathKeys) setSelectionRectPathKeys([])

    let newPaths = {}
    let eraseStack = { type: 'erase', pathKeys: [], pathData: {} }

    yPathsRef.current.clear()
    Object.entries(paths).forEach(([key, path]) => {
      if (path.isLocked === true) {
        newPaths[key] = path
        triggerAddPath(key, path) // 잠금 예외처리 잘 동작하는지 확인해야 함
      } else {
        eraseStack.pathKeys.push(key)
        eraseStack.pathData[key] = path
      }
    })

    setPaths({ ...newPaths })
    updateHistoryStackReset({ boardId, eraseStack, setHistoryStack })
  }

  const handleUndo = () => {
    if (historyStack[boardId].pointer === -1) return
    if (selectionRectPathKeys.length) setSelectionRectPathKeys([])

    const currentStack = historyStack[boardId].stack[historyStack[boardId].pointer]

    // [historyStack type : draw]
    if (currentStack.type === 'draw') {
      delete paths[currentStack.pathKeys]
      yPathsRef.current.delete(currentStack.pathKeys)
    }
    // [historyStack type : erase]
    else if (currentStack.type === 'erase') {
      ydocRef.current.transact(() => {
        for (let pathKey of currentStack.pathKeys) {
          paths[pathKey] = currentStack.pathData[pathKey]
          triggerAddPath(pathKey, currentStack.pathData[pathKey])
        }
      })
    }
    // [historyStack type : select(move && resize)]
    else if (currentStack.type === 'select') {
      ydocRef.current.transact(() => {
        for (let pathKey of currentStack.pathKeys) {
          paths[pathKey] = currentStack.beforePathData[pathKey]
          triggerUpdatePath(pathKey, currentStack.beforePathData[pathKey], 'replace')
        }
      })
    }
    // [historyStack type : image]
    else if (currentStack.type === 'image') {
      delete paths[currentStack.pathKeys]
      yPathsRef.current.delete(currentStack.pathKeys)

      if (lockedCheckKey) setLockedCheckKey('')
      setSelectionRectPathKeys([])
    }

    historyStack[boardId].pointer -= 1
  }

  const handleRedo = () => {
    if (historyStack[boardId].pointer === historyStack[boardId].stack.length - 1) return
    if (selectionRectPathKeys.length) setSelectionRectPathKeys([])

    historyStack[boardId].pointer += 1
    const currentStack = historyStack[boardId].stack[historyStack[boardId].pointer]

    // [historyStack type : draw]
    if (currentStack.type === 'draw') {
      paths[currentStack.pathKeys] = currentStack.pathData
      triggerAddPath(currentStack.pathKeys, currentStack.pathData)
    }
    // [historyStack type : erase]
    else if (currentStack.type === 'erase') {
      ydocRef.current.transact(() => {
        for (let pathKey of currentStack.pathKeys) {
          delete paths[pathKey]
          yPathsRef.current.delete(pathKey)
        }
      })
    }
    // [historyStack type : select(move && resize)]
    else if (currentStack.type === 'select') {
      ydocRef.current.transact(() => {
        for (let pathKey of currentStack.pathKeys) {
          paths[pathKey] = currentStack.afterPathData[pathKey]
          triggerUpdatePath(pathKey, currentStack.afterPathData[pathKey], 'replace')
        }
      })
    }
    // [historyStack type : image]
    else if (currentStack.type === 'image') {
      triggerAddPath(currentStack.pathKeys, currentStack.pathData)
    }
  }

  return (
    <div
      className={`sg-h-28 md:sg-h-40 xl:sg-h-52 sg-absolute sg-left-8 sg-bottom-8 md:sg-left-12 md:sg-bottom-12 xl:sg-left-16 xl:sg-bottom-12 sg-z-[99999] ${
        isWritable ? 'sg-block' : 'sg-hidden'
      }`}
    >
      <div className='sg-h-full sg-flex sg-items-center sg-justify-center sg-rounded-[6px] xl:sg-rounded-[10px] sg-border sg-xl:border-2 sg-border-solid sg-border-sk-border-default sg-bg-sk-white sg-shadow-emphasize'>
        <div className='sg-flex sg-items-center sg-justify-center sg-gap-8 sg-px-6 md:sg-gap-12 xl:sg-px-8 xl:sg-gap-16'>
          <button
            className='sg-w-20 sg-h-20 md:sg-w-28 md:sg-h-28 xl:sg-w-38 xl:sg-h-38 sg-rounded-[4px] md:sg-rounded-[6px] xl:sg-rounded-[8px] sg-bg-sk-background-normal-alternative sg-text-sk-label-info sg-flex sg-items-center sg-justify-center'
            onClick={(e) => zoomInOut('Out')}
          >
            <i className='sg-block md:sg-hidden'>
              <SubtractSmallIcon />
            </i>
            <i className='sg-hidden md:sg-block xl:sg-hidden'>
              <SubtractMediumIcon />
            </i>
            <i className='sg-hidden xl:sg-block'>
              <SubtractLargeIcon />
            </i>
          </button>

          <div className='sg-flex sg-items-center sg-justify-center sg-w-[25px] md:sg-w-[30px]'>
            <p className='sg-text-caption2-medium md:sg-text-body2-medium xl:sg-text-h2-medium sg-text-sk-label-info'>
              {Math.floor((scope / scaleRatio.current) * 100)}%
            </p>
          </div>

          <button
            className='sg-w-20 sg-h-20 md:sg-w-28 md:sg-h-28 xl:sg-w-38 xl:sg-h-38 sg-rounded-[4px] md:sg-rounded-[6px] xl:sg-rounded-[8px] sg-bg-sk-background-normal-alternative sg-text-sk-label-info sg-flex sg-items-center sg-justify-center'
            onClick={(e) => zoomInOut('In')}
          >
            <i className='sg-block md:sg-hidden'>
              <AddSmallIcon />
            </i>
            <i className='sg-hidden md:sg-block xl:sg-hidden'>
              <AddMediumIcon />
            </i>
            <i className='sg-hidden xl:sg-block'>
              <AddLargeIcon />
            </i>
          </button>
        </div>

        <div className='sg-flex sg-items-center sg-justify-center sg-gap-8 sg-px-6 sg-border-l sg-border-solid md:sg-gap-12 xl:sg-px-8 xl:sg-gap-16 sg-border-sk-border-default'>
          <button
            className={`sg-w-20 sg-h-20 md:sg-w-28 md:sg-h-28 xl:sg-w-38 xl:sg-h-38 sg-flex sg-items-center sg-justify-center sg-rounded-[4px] md:sg-rounded-[6px] xl:sg-rounded-[8px] ${
              historyStack[boardId] && historyStack[boardId].pointer !== -1
                ? 'sg-bg-sk-background-alternative sg-text-sk-label-info sg-cursor-pointer'
                : 'sg-bg-sk-background-normal-alternative sg-text-sk-label-disable sg-cursor-default'
            }`}
            onClick={handleUndo}
          >
            <i className='sg-block md:sg-hidden'>
              <UndoSmallIcon />
            </i>
            <i className='sg-hidden md:sg-block xl:sg-hidden'>
              <UndoMediumIcon />
            </i>
            <i className='sg-hidden xl:sg-block'>
              <UndoLargeIcon />
            </i>
          </button>
          <button
            className={`sg-w-20 sg-h-20 md:sg-w-28 md:sg-h-28 xl:sg-w-38 xl:sg-h-38 sg-flex sg-items-center sg-justify-center sg-rounded-[4px] md:sg-rounded-[6px] xl:sg-rounded-[8px] ${
              historyStack[boardId] &&
              historyStack[boardId].pointer !== historyStack[boardId].stack.length - 1
                ? 'sg-bg-sk-background-alternative sg-text-sk-label-info sg-cursor-pointer'
                : 'sg-bg-sk-background-normal-alternative sg-text-sk-label-disable sg-cursor-default'
            }`}
            onClick={handleRedo}
          >
            <i className='sg-block md:sg-hidden'>
              <RedoSmallIcon />
            </i>
            <i className='sg-hidden md:sg-block xl:sg-hidden'>
              <RedoMediumIcon />
            </i>
            <i className='sg-hidden xl:sg-block'>
              <RedoLargeIcon />
            </i>
          </button>
        </div>

        <div className='sg-relative sg-flex sg-items-center sg-justify-center sg-gap-8 sg-px-6 sg-border-l sg-border-solid xl:sg-px-8 sg-border-sk-border-default'>
          <button
            className='sg-flex sg-items-center sg-justify-center sg-w-20 sg-h-20 md:sg-w-28 md:sg-h-28 xl:sg-w-38 xl:sg-h-38 sg-cursor-pointer sg-rounded-[4px] md:sg-rounded-[6px] xl:sg-rounded-[8px] sg-bg-sk-background-alternative sg-text-sk-label-info'
            onClick={() => setIsMoreButtonVisible((prev) => !prev)}
          >
            <i className='sg-block md:sg-hidden'>
              <MoreSmallIcon />
            </i>
            <i className='sg-hidden md:sg-block xl:sg-hidden'>
              <MoreMediumIcon />
            </i>
            <i className='sg-hidden xl:sg-block'>
              <MoreLargeIcon />
            </i>
          </button>

          {isMoreButtonVisible && (
            <button
              className='sg-absolute sg-w-[100px] md:sg-w-[130px] xl:sg-w-[160px] sg-top-[-38px] sg-right-[-10px] md:sg-top-[-50px] xl:sg-top-[-65px] sg-translate-x-1/4 sg-flex sg-justify-center sg-items-center sg-gap-4 sg-pl-4 sg-pr-6 sg-py-4 xl:sg-py-6 sg-text-[#FF4242] sg-rounded-[4px] md:sg-rounded-[6px] xl:sg-rounded-[8px] sg-border sg-border-solid sg-bg-sk-white sg-shadow-emphasize'
              onClick={onClickReset}
            >
              <div className='sg-flex sg-items-center sg-justify-center sg-w-20 sg-h-20 md:sg-w-28 md:sg-h-28 xl:sg-w-38 xl:sg-h-38'>
                <i className='sg-block md:sg-hidden'>
                  <TrashSmallIcon />
                </i>
                <i className='sg-hidden md:sg-block xl:sg-hidden'>
                  <TrashMediumIcon />
                </i>
                <i className='sg-hidden xl:sg-block'>
                  <TrashLargeIcon />
                </i>
              </div>
              <p className='sg-text-caption2-regular md:sg-text-body2-regular xl:sg-text-body1-regular'>
                필기 전체 삭제
              </p>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
